import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { adminService, Hospital } from '@/services/adminService';
import { 
  Hospital as HospitalIcon, 
  Plus, 
  Edit, 
  Search,
  AlertTriangle,
  Check,
  MapPin,
  Phone,
  Mail,
  Users,
  Activity
} from 'lucide-react';

export const HospitalsManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    hospital_name: '',
    hospital_code: '',
    city: '',
    county: '',
    address: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    loadHospitals();
  }, []);

  const loadHospitals = async () => {
    try {
      setError(null);
      const data = await adminService.listHospitals();
      setHospitals(data);
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading hospitals:', err);
      setError(err.message || 'Eroare la încărcarea spitalelor');
      setLoading(false);
    }
  };

  const handleAddHospital = async () => {
    try {
      setError(null);
      
      // Validate required fields
      if (!formData.hospital_name || !formData.hospital_code) {
        setError('Numele și codul spitalului sunt obligatorii');
        return;
      }
      
      await adminService.createHospital(formData);
      setSuccessMessage('Spital adăugat cu succes!');
      setShowAddDialog(false);
      resetForm();
      await loadHospitals();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la adăugarea spitalului');
    }
  };

  const handleEditHospital = async () => {
    if (!selectedHospital) return;
    
    try {
      setError(null);
      await adminService.updateHospital(selectedHospital.id || selectedHospital.hospital_id || 0, formData);
      setSuccessMessage('Spital actualizat cu succes!');
      setShowEditDialog(false);
      resetForm();
      await loadHospitals();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la actualizarea spitalului');
    }
  };

  const resetForm = () => {
    setFormData({
      hospital_name: '',
      hospital_code: '',
      city: '',
      county: '',
      address: '',
      phone: '',
      email: ''
    });
    setSelectedHospital(null);
  };

  const openEditDialog = (hospital: Hospital) => {
    setSelectedHospital(hospital);
    setFormData({
      hospital_name: hospital.name || hospital.hospital_name || '',
      hospital_code: hospital.code || hospital.hospital_code || '',
      city: hospital.city || '',
      county: hospital.county || '',
      address: hospital.address || '',
      phone: hospital.phone || '',
      email: hospital.email || ''
    });
    setShowEditDialog(true);
  };

  // Filter hospitals
  const filteredHospitals = hospitals.filter(hospital => {
    const searchLower = searchTerm.toLowerCase();
    return (hospital.name || hospital.hospital_name || '').toLowerCase().includes(searchLower) ||
           (hospital.code || hospital.hospital_code || '').toLowerCase().includes(searchLower) ||
           hospital.city?.toLowerCase().includes(searchLower) ||
           hospital.county?.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Gestionare spitale</h1>
            <p className="text-muted-foreground">
              Administrează unitățile medicale din sistem
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
                <Plus className="mr-2 h-4 w-4" />
                Adaugă spital
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[525px]">
              <DialogHeader>
                <DialogTitle>Adaugă spital nou</DialogTitle>
                <DialogDescription>
                  Completează informațiile pentru noul spital
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="hospital_name">Nume spital *</Label>
                  <Input
                    id="hospital_name"
                    value={formData.hospital_name}
                    onChange={(e) => setFormData({...formData, hospital_name: e.target.value})}
                    placeholder="ex: Spitalul Județean Cluj"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="hospital_code">Cod spital *</Label>
                  <Input
                    id="hospital_code"
                    value={formData.hospital_code}
                    onChange={(e) => setFormData({...formData, hospital_code: e.target.value})}
                    placeholder="ex: SJC-001"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="city">Oraș</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="ex: Cluj-Napoca"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="county">Județ</Label>
                    <Input
                      id="county"
                      value={formData.county}
                      onChange={(e) => setFormData({...formData, county: e.target.value})}
                      placeholder="ex: Cluj"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="address">Adresă</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="ex: Str. Clinicilor Nr. 5"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="ex: 0264-123456"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="ex: contact@spital.ro"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Anulează
                </Button>
                <Button onClick={handleAddHospital}>Adaugă</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {successMessage && (
          <Alert className="border-green-500 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total spitale</CardTitle>
              <HospitalIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{hospitals.length}</div>
              <p className="text-xs text-muted-foreground">Unități medicale înregistrate</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Spitale active</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {hospitals.filter(h => h.is_active).length}
              </div>
              <p className="text-xs text-muted-foreground">Cu activitate recentă</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total utilizatori</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {hospitals.reduce((sum, h) => sum + (h.user_count || 0), 0)}
              </div>
              <p className="text-xs text-muted-foreground">În toate spitalele</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total pacienți</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {hospitals.reduce((sum, h) => sum + (h.patient_count || 0), 0)}
              </div>
              <p className="text-xs text-muted-foreground">Cazuri înregistrate</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle>Căutare</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Caută după nume, cod, oraș sau județ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
            </div>
          </CardContent>
        </Card>

        {/* Hospitals Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              Lista spitalelor ({filteredHospitals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nume spital</TableHead>
                  <TableHead>Cod</TableHead>
                  <TableHead>Locație</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Utilizatori</TableHead>
                  <TableHead>Pacienți</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data înregistrării</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHospitals.map((hospital) => (
                  <TableRow key={hospital.id || hospital.hospital_id}>
                    <TableCell className="font-medium">{hospital.name || hospital.hospital_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{hospital.code || hospital.hospital_code}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{hospital.city || '-'}</span>
                        <span className="text-xs text-muted-foreground">{hospital.county || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {hospital.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs">{hospital.phone}</span>
                          </div>
                        )}
                        {hospital.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs">{hospital.email}</span>
                          </div>
                        )}
                        {!hospital.phone && !hospital.email && <span className="text-xs text-muted-foreground">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span>{hospital.user_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Activity className="h-3 w-3 text-muted-foreground" />
                        <span>{hospital.patient_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={hospital.is_active ? 'default' : 'secondary'}>
                        {hospital.is_active ? 'Activ' : 'Inactiv'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(hospital.created_at).toLocaleDateString('ro-RO')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(hospital)}
                        aria-label={`Editează ${hospital.name || hospital.hospital_name}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[525px]">
            <DialogHeader>
              <DialogTitle>Editează spital</DialogTitle>
              <DialogDescription>
                Modifică informațiile spitalului
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-hospital_name">Nume spital *</Label>
                <Input
                  id="edit-hospital_name"
                  value={formData.hospital_name}
                  onChange={(e) => setFormData({...formData, hospital_name: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-hospital_code">Cod spital *</Label>
                <Input
                  id="edit-hospital_code"
                  value={formData.hospital_code}
                  onChange={(e) => setFormData({...formData, hospital_code: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-city">Oraș</Label>
                  <Input
                    id="edit-city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-county">Județ</Label>
                  <Input
                    id="edit-county"
                    value={formData.county}
                    onChange={(e) => setFormData({...formData, county: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-address">Adresă</Label>
                <Input
                  id="edit-address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-phone">Telefon</Label>
                  <Input
                    id="edit-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Anulează
              </Button>
              <Button onClick={handleEditHospital}>Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};